/** Missing or false = included in Gross Profit. Only an explicit true excludes. */
export const isVenueProductExcludedFromGrossProfit = product =>
  product?.excludeFromGrossProfit === true

export const EXCLUDE_FROM_GROSS_PROFIT_COLUMN_LABEL =
  'Exclude from gross profit'

export const EXCLUDE_FROM_GROSS_PROFIT_HEADER_LINES = [
  'Exclude',
  'from gross',
  'profit',
]

export const EXCLUDE_FROM_GROSS_PROFIT_HELPER =
  'You can still order and receipt this product. It drops out of Gross Profit purchases, including when ordered as a special. One-off specials with no catalogue product stay in Gross Profit.'

export const EXCLUDE_FROM_GROSS_PROFIT_TOOLTIP =
  EXCLUDE_FROM_GROSS_PROFIT_HELPER

export const GR_EXCLUDE_FROM_GP_UNAVAILABLE_TOOLTIP =
  'This line is not on the shopping list, so it stays in Gross Profit. You can still order and receipt it.'

export const GR_EXCLUDE_FROM_GP_ADD_TO_LIST_LABEL = 'Add to shopping list'

export const GR_EXCLUDE_FROM_GP_ADD_TO_LIST_TOOLTIP =
  'This line is not on the shopping list. Add it to exclude from Gross Profit. You can still order and receipt it.'

export const THIS_SUPPLIER_LABEL = 'this supplier'

export const normalizeShoppingListTitle = title =>
  String(title ?? '')
    .trim()
    .toLowerCase()

const venueProductId = product =>
  product?._id == null ? '' : String(product._id)

export const getVenueProductSupplierLabel = (
  product,
  { fallback = THIS_SUPPLIER_LABEL } = {},
) => {
  const fromTitle = String(product?.supplierTitle ?? '').trim()
  if (fromTitle) return fromTitle

  const nested = product?.supplier
  if (nested && typeof nested === 'object') {
    const fromObject = String(nested.title ?? nested.name ?? '').trim()
    if (fromObject) return fromObject
  }

  return fallback
}

export const getDuplicateTitleVenueProducts = (products, product) => {
  const title = normalizeShoppingListTitle(product?.title)
  const id = venueProductId(product)
  if (!title || !id) return []

  return (products || []).filter(
    other =>
      venueProductId(other) &&
      venueProductId(other) !== id &&
      normalizeShoppingListTitle(other?.title) === title,
  )
}

export const hasDuplicateShoppingListTitle = (products, product) =>
  getDuplicateTitleVenueProducts(products, product).length > 0

export const getExcludeFromGrossProfitDuplicateTitleCopy = ({
  product,
  products,
  excluding = true,
} = {}) => {
  const duplicates = getDuplicateTitleVenueProducts(products, product)
  if (!duplicates.length) return null

  const supplierLabel = getVenueProductSupplierLabel(product)
  const title = String(product?.title ?? '').trim() || 'this product'
  const otherLabel =
    duplicates.length === 1
      ? '1 other shopping-list row shares this title'
      : `${duplicates.length} other shopping-list rows share this title`

  if (excluding) {
    return {
      title: `Exclude ${title} from ${supplierLabel} only?`,
      body: `${otherLabel}. Only ${supplierLabel} will drop out of Gross Profit.`,
      okText: `Exclude ${supplierLabel}`,
      cancelText: 'Cancel',
      supplierLabel,
    }
  }

  return {
    title: `Include ${title} from ${supplierLabel} in Gross Profit?`,
    body: `${otherLabel}. Only ${supplierLabel} will be included again.`,
    okText: `Include ${supplierLabel}`,
    cancelText: 'Cancel',
    supplierLabel,
  }
}

export const getExcludeFromGrossProfitDuplicateTitleHint = ({
  product,
  products,
} = {}) => {
  if (!hasDuplicateShoppingListTitle(products, product)) return null
  return `Other shopping-list rows share this title. Ticking only drops ${getVenueProductSupplierLabel(
    product,
  )} from Gross Profit.`
}

export const getExcludeFromGrossProfitDuplicateTitleTooltip = ({
  product,
  products,
} = {}) => {
  if (!hasDuplicateShoppingListTitle(products, product)) return null
  const supplierLabel = getVenueProductSupplierLabel(product)
  return `This row is ${supplierLabel}. Other rows share this title — only ${supplierLabel} drops out of Gross Profit.`
}

export const getExcludeFromGrossProfitAriaLabel = ({
  product,
  products,
} = {}) => {
  const title = String(product?.title ?? '').trim() || 'product'
  const namedSupplier = getVenueProductSupplierLabel(product, { fallback: '' })
  if (hasDuplicateShoppingListTitle(products, product) && namedSupplier) {
    return `${EXCLUDE_FROM_GROSS_PROFIT_COLUMN_LABEL} for ${title} (${namedSupplier})`
  }
  return `${EXCLUDE_FROM_GROSS_PROFIT_COLUMN_LABEL} for ${title}`
}
